import 'flag-icons/css/flag-icons.css';

import { createGlobalStyle } from 'styled-components';
import reset from 'styled-reset';

import type { Fonts } from '@/theme/types/fonts';

import { breakpoints } from './breakpoints';
import { colors } from './colors';
import { components } from './components';
import { fontFamilies } from './fontFamilies';
import { fontSizes } from './fontSizes';
import { fontWeights } from './fontWeights';
import { gridColumns } from './gridColumns';
import { gridContainer } from './gridContainer';
import { gridGap } from './gridGap';
import { lineHeights } from './lineHeights';
import { maxWidth } from './maxWidth';
import { media } from './media';
import { mediaQueriesLegacy } from './mediaQueriesLegacy';
import { motion } from './motion';
import { radii } from './radii';
import { space } from './space';
import { transitions } from './transitions';
import type { Theme } from './types';
import { generateSpacings } from './utils';
import { zIndex } from './zIndex';

export { breakpoints } from './breakpoints';
export { colors } from './colors';
export { components } from './components';
export { fontFamilies } from './fontFamilies';
export { fontSizes } from './fontSizes';
export { fontWeights } from './fontWeights';
export { gridColumns } from './gridColumns';
export { gridContainer } from './gridContainer';
export { gridGap } from './gridGap';
export { lineHeights } from './lineHeights';
export { maxWidth } from './maxWidth';
export { media } from './media';
export { mediaQueriesLegacy } from './mediaQueriesLegacy';
export { motion } from './motion';
export { radii } from './radii';
export { space } from './space';
export { transitions } from './transitions';
export { zIndex } from './zIndex';
export const fonts: Fonts = {
  paragraphFont: fontFamilies.openSans as string,
  subtitleFont: fontFamilies.italianPlate as string,
  titleFont: fontFamilies.italianPlate as string,
};

export const theme: Theme = {
  breakpoints,
  maxWidth,
  colors,
  fontFamilies,
  fontSizes,
  media,
  fontWeights,
  gridColumns,
  gridContainer,
  gridGap,
  lineHeights,
  mediaQueries: mediaQueriesLegacy,
  motion,
  radii,
  space,
  components,
  transitions,
  zIndex,
  spacing: generateSpacings,
  fonts,
};

export const spacing = generateSpacings;

export const GlobalStyle = createGlobalStyle`
  ${reset}

  body {
    display: block !important;
    background-color: ${theme.colors.white};
  }

  body:has(.modal-open) {
    overflow: hidden !important;
  }

  body,
  html,
  * {
    font-family: ${fontFamilies.openSans}, sans-serif;
  }

  * {
    box-sizing: border-box;
  }

  button {
    border: 0;
    padding: 1rem;
    margin: 0;
    text-decoration: none;
    font-size: 1rem;
    font-family: inherit;
    cursor: pointer;
    appearance: none;
  }

  a {
   color: inherit;
  }

  strong {
    font-weight: ${theme.fontWeights.bold};
  }

  /**
   * Prevent ios to zoom automatically on focus. Safari considers that a font size under 16px is too small to be readable.
   */
  input, textarea, select { font-size: ${theme.fontSizes.p1}; }

  /**
   * Photoswipe
   */
  .pswp {
    --pswp-bg: ${theme.colors.white};
    --pswp-placeholder-bg: ${theme.colors.white};
  }

  /**
   * Adyen
   */
  .adyen-checkout {
    &__field {
       position: relative;

      /**
       * Holder name label doesn't receive --valid class...
       */
      &--valid .adyen-checkout__label {
        transform: translateY(5px);

        span {
          font-size: ${({ theme }) => theme.fontSizes.p5};
        }
      }
    }

    &__label {
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      transform: translateY(15px);
      pointer-events: none;
      padding: ${theme.spacing(0, 1)};
      transition: ${theme.transitions.create('transform')};

      span {
        transition: ${theme.transitions.create('font-size')};
        padding: 0;
      }

      &--focused,
      &--filled,
      &--valid {
        transform: translateY(5px);

        span {
          font-size: ${({ theme }) => theme.fontSizes.p5};
        }
      }

      &--focused  span {
        color: inherit !important;
      }

      &__text {
        overflow: visible;
      }
    }

    &__input {
      background-color: ${theme.colors.lightGrey};
      border-radius: 0;
      border: none;
      font-size: ${theme.fontSizes.p1};
      font-family: ${theme.fontFamilies.openSans};
      caret-color: unset;
      outline: none;
      
      span&{
        overflow: unset;
      }

      &-wrapper {
        border: none;
        border-radius: 0;
        border-bottom: 1px solid ${theme.colors.secondaryGrey};
        font-size: ${theme.fontSizes.p1};
        font-family: ${theme.fontFamilies.openSans};
        background-color: ${theme.colors.lightGrey};
        box-shadow: none;
        caret-color: unset;

        span:first-child {
          line-height: 60px;
        }

        iframe {
          margin-top: 10px;
        }

        input {
          padding-top: 17px;
          font-size: ${theme.fontSizes.p1};
        }

        &:focus-within, &:focus-within:hover {
          box-shadow: none;
        }

        .adyen-checkout__label--focused+ & {
          box-shadow: none;
        }
        
      }

      &--focus,
      &--focus:hover,
      &:active,
      &:active:hover,
      &:focus,
      &:focus:hover {
        border: none;
        box-shadow: none;
        outline: none;
      }

      &:focus,
      &:active {
        outline: none;
      }
    }

    &__card__brands {
      justify-content: flex-end
    }

    &-form-instruction {
      display: none;
    }
  }
  .next-image {
    transition: ${theme.transitions.create('opacity')};
    will-change: opacity, position;
    .next-image_loading > & {
      position: absolute;
      opacity: 0;
      inset: 0;
    }
  }
  .tooltip-multiple-line {
    z-index: 999;
    white-space: normal;
    max-width: 80vw;
  }
`;
